import brandLovers from '@assets/img/svg/solution/brandlovers.svg'
import performace from '@assets/img/svg/solution/performace.svg'
import research from '@assets/img/svg/solution/research.svg'
import IconsEnum from '@enums/icons.enum'
import {
  Button,
  Campaign,
  CategoriesHelper,
  Category,
  ColorIconHelper,
  StringsHelper,
  Tag,
  UserInDetails,
  defaultImage,
  onLangChange,
} from '@hub/shared'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import './campaign-head.component.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  campaign?: Campaign
  user?: UserInDetails
  mode?: 'details' | 'summary' | 'list' | 'tiny'
}

export default ({ className = '', style = {}, id = '', campaign, mode = 'list', user }: Props) => {
  const onLangChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const categoriesHelper = useMemo(() => new CategoriesHelper(), [])
  const stringsHelper = useMemo(() => new StringsHelper(), [])
  const iconsEnum = useMemo(() => IconsEnum, [])
  const [categories, setCategories] = useState<Category[]>([])
  const [lang, setLang] = useState<string>('en')
  const { t, i18n } = useTranslation('campaignHead')
  const colorIconHelper = new ColorIconHelper()
  const [coverIsError, setCoverIsError] = useState<boolean>(false)
  const [imageLoaded, setImageLoaded] = useState<boolean>(false)

  const iconsMapped = useMemo(
    () => ({
      contents: 'fa-file-alt',
      posts: 'fa-image-polaroid',
      reels: 'fa-film',
      stories: 'fa-rectangle-vertical-history',
    }),
    []
  )

  let startDate = useMemo(() => {
    return new Date(campaign?.startDate || campaign?.activation?.startDate)
  }, [campaign?.startDate, campaign?.activation?.startDate])

  let endDate = useMemo(() => {
    return new Date(campaign?.endDate || campaign?.activation?.endDate)
  }, [campaign?.endDate, campaign?.activation?.endDate])

  useEffect(() => {
    categoriesHelper.getCategories().then((allCategories) => {
      setCategories(allCategories)
    })
    onLangChangeSubscription.current = onLangChange.subscribe((lang) => {
      setLang(lang)
    })
    return () => {
      onLangChangeSubscription.current?.unsubscribe()
    }
  }, [categoriesHelper])

  const getTagsScope = useCallback(() => {
    if (user?.scope?.requiredContent && Object?.values(user?.scope?.requiredContent)?.some((value) => !!value)) {
      return Object?.entries(user?.scope?.requiredContent).map(([key, count]) => {
        if (count) {
          return <Tag key={key} icon={<i className={`fa-light ${iconsMapped[key]}`}></i>} labels={[t(`fields.${key}`, { count })]} />
        }
      })
    }
    return null
  }, [iconsMapped, t, user?.scope?.requiredContent])

  const getCategoryByLang = useCallback(
    (category: Category) => {
      if (!category) {
        return null
      }
      if (lang.includes('pt')) {
        return category?.description
      }
      return (category as any)[`description_${lang}`]
    },
    [lang]
  )

  const parseStatus = (status: string) => {
    switch (status) {
      case 'planning':
        return 'soon'
      case 'briefing':
        return 'soon'
      case 'recruitment':
        return 'soon'
      case 'in_progress':
        return 'inProgress'
      case 'finished':
        return 'finished'
      case 'archived':
        return 'finished'
      default:
        return 'finished'
    }
  }

  const parseStatusColor = (status: string) => {
    switch (status) {
      case 'planning':
        return 'pastry-dark'
      case 'briefing':
        return 'pastry-dark'
      case 'recruitment':
        return 'pastry-dark'
      case 'in_progress':
        return 'green'
      case 'finished':
        return 'var(--text_color)'
      case 'archived':
        return 'var(--text_color)'
      default:
        return 'var(--text_color)'
    }
  }

  const parseStatusIcon = (status: string) => {
    switch (status) {
      case 'planning':
        return 'fa-regular fa-circle-pause'
      case 'briefing':
        return 'fa-regular fa-circle-pause'
      case 'recruitment':
        return 'fa-regular fa-circle-pause'
      case 'in_progress':
        return 'fa-solid fa-circle-play'
      case 'finished':
        return 'fa-solid fa-flag-checkered'
      case 'archived':
        return 'fa-solid fa-flag-checkered'
      default:
        return 'fa-solid fa-flag-checkered'
    }
  }

  const parseSolutionIcon = (solution: string) => {
    switch (solution) {
      case 'Squid Influencers':
        return ''
      case 'Squid Seeding':
        return ''
      case 'Squid Brand Lovers':
        return brandLovers
      case 'Affiliates':
        return performace
      case 'Survey':
        return research
      default:
        return ''
    }
  }

  const renderCategory = useCallback(
    (asString = false) => {
      if (asString) {
        return campaign?.activation?.categories
          ?.map((category, index) => {
            const categoryObj = categories.find((c) => c.id === category)
            if (categoryObj) {
              return `${index > 0 ? ' • ' : ''}${getCategoryByLang(categoryObj)}`
            }
            return ''
          })
          .filter((c) => c?.length)
          .join('')
      }
      return campaign?.activation?.categories?.map((category, index) => {
        const categoryObj = categories.find((c) => c.id === category)
        if (categoryObj) {
          return (
            <span key={category}>
              {index > 0 ? <span className="bullet">•</span> : ''}
              {getCategoryByLang(categoryObj)}
            </span>
          )
        }
        return ''
      })
    },
    [categories, campaign, getCategoryByLang]
  )

  const toNPS = useCallback(() => {
    if (campaign?.nps?.linkNps) {
      window.open(campaign?.nps?.linkNps, '_blank')
    }
  }, [campaign?.nps?.linkNps])

  const handleClick = useCallback(
    (e: Event) => {
      e.preventDefault()
      e.stopPropagation()
      toNPS()
    },
    [toNPS]
  )

  const handleFormatUrl = useCallback((cdnUrl: string, url: string) => {
    return url && url?.startsWith('http') ? url : cdnUrl + url
  }, [])

  if (!campaign) {
    return <div />
  }

  const headerFull = (
    <div>
      <header>
        <figure
          className="bg"
          style={{
            backgroundColor: campaign?.activation?.coverImage?.color || 'var(--background)',
          }}
        >
          {!coverIsError ? (
            <img
              alt="Campaign Cover"
              src={`${campaign?.activation?.coverImage?.cdnUrl + campaign?.activation?.coverImage?.url}`}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null
                currentTarget.src = null
                setCoverIsError(true)
              }}
              onLoad={() => setImageLoaded(true)}
              style={{ display: imageLoaded ? 'block' : 'none' }}
            />
          ) : null}
        </figure>
        <span className="wrapper">
          {' '}
          <div className="wrapper-avatar">
            <figure className="avatar">
              <img
                src={
                  campaign?.activation?.brandLogo
                    ? handleFormatUrl(campaign?.activation?.brandPicture?.cdnUrl, campaign?.activation?.brandPicture?.url)
                    : handleFormatUrl(campaign?.activation?.logo?.cdnUrl, campaign?.activation?.logo?.url)
                }
                className="img-fluid"
                title="Campaign Avatar"
                alt="Campaign Avatar"
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null
                  currentTarget.src = defaultImage
                }}
              />
            </figure>
            {mode === 'summary' || mode === 'details' ? (
              <h1 title={campaign?.exhibitionName || campaign?.title}>{campaign?.exhibitionName || campaign?.title}</h1>
            ) : (
              <h6 title={campaign?.exhibitionName || campaign?.title}>{campaign?.exhibitionName || campaign?.title}</h6>
            )}
          </div>
        </span>
      </header>
      {campaign?.activation?.categories?.length && categories?.length ? (
        <div className="categories mb-3" title={renderCategory(true) as string}>
          {renderCategory()}
        </div>
      ) : (
        <div className="categories mb-3" />
      )}
    </div>
  )

  let content = (
    <div>
      {headerFull}
      <div className="content">
        {user && user?.status === 'recruited' ? (
          <div className="user-in-campaign">
            <figure>
              <img
                src={`https://storage.googleapis.com/squid-profile-pictures/${user?.idNetwork}.jpg`}
                className="img-fluid"
                title={user?.username || 'User Avatar'}
                alt={user?.username || 'User Avatar'}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null
                  currentTarget.src = defaultImage
                }}
              />
              <i className="fa-solid fa-circle-check check"></i>
            </figure>
            <p className="mb-0">{['finished', 'archived'].includes(campaign?.status) ? t('youParticipated') : t('joined')}</p>
          </div>
        ) : null}
        <div className="tags">
          {campaign?.planning?.solution &&
          !['Squid Influencers', 'Squid Seeding', 'Squid Brand Community'].includes(campaign?.planning?.solution) ? (
            <Tag
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: '0.9rem',
              }}
              labels={t(`solution.${campaign?.planning?.solution}`)}
              icon={
                <img
                  className="img-fluid icon-tag"
                  alt="Campaign Solution"
                  src={parseSolutionIcon(campaign?.planning?.solution)}
                  onError={({ currentTarget }) => {
                    currentTarget.style.display = 'none'
                  }}
                />
              }
            />
          ) : null}
          <Tag
            style={{
              padding: '0.25rem 0.5rem',
              fontSize: '0.9rem',
            }}
            labels={stringsHelper?.capitalizeFirstLetter(campaign?.socialNetworks[0])}
            icon={
              <i
                className={`icon-tag ${
                  !['tiktok', 'twitter'].includes(campaign?.socialNetworks[0]) ? campaign?.socialNetworks[0] : ''
                } fa-brands fa-${colorIconHelper.setIcon(campaign?.socialNetworks[0])}`}
              ></i>
            }
          />
          <Tag
            style={{
              padding: '0.25rem 0.5rem',
              fontSize: '0.9rem',
            }}
            labels={t(parseStatus(campaign?.status))}
            icon={<i className={`icon-tag ${parseStatusColor(campaign?.status)} ${parseStatusIcon(campaign?.status)}`}></i>}
          />
          {campaign?.startDate || campaign?.activation?.startDate ? (
            <Tag
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: '0.9rem',
              }}
              // ? Os campos campaign.startDate e campaign.endDate são as datas de REALIZAÇÃO da campanhas
              //    * Esses valores estão vindo com timezone 0
              // ? Os campos campaign.activation.starDate e campaign.activation.endDate são as datas PREVISTAS da campanha
              //    * Esses valores estão vindo com timezone +3
              labels={`${t('startDate')} - ${new Date(startDate).toLocaleDateString(i18n.language, {
                timeZone: startDate?.getUTCHours() === 0 ? 'utc' : campaign?.activation?.timezone,
              })}`}
              icon={<i className={`icon-tag ${iconsEnum.startDate}`}></i>}
            />
          ) : null}
          {campaign?.endDate || campaign?.activation?.endDate ? (
            <Tag
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: '0.9rem',
              }}
              // ? Os campos campaign.startDate e campaign.endDate são as datas de REALIZAÇÃO da campanhas
              //    * Esses valores estão vindo com timezone 0
              // ? Os campos campaign.activation.starDate e campaign.activation.endDate são as datas PREVISTAS da campanha
              //    * Esses valores estão vindo com timezone +3
              labels={`${t('endDate')} - ${new Date(endDate).toLocaleDateString(i18n.language, {
                timeZone: endDate?.getUTCHours() === 0 ? 'utc' : campaign?.activation?.timezone,
              })}`}
              icon={<i className={`icon-tag ${iconsEnum.endDate}`}></i>}
            />
          ) : null}
          {!user?.scope && campaign?.activation?.postsCount ? (
            <Tag
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: '0.9rem',
              }}
              labels={`${campaign?.activation?.postsCount} Posts`}
              icon={<i className={`icon-tag ${iconsEnum.post}`}></i>}
            />
          ) : null}
          {!user?.scope && campaign?.activation?.storiesCount ? (
            <Tag
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: '0.9rem',
              }}
              labels={`${campaign?.activation?.storiesCount} Stories`}
              icon={<i className={`icon-tag ${iconsEnum.stories}`}></i>}
            />
          ) : null}
          {!user?.scope && campaign?.activation?.reelsCount ? (
            <Tag
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: '0.9rem',
              }}
              labels={`${campaign?.activation?.reelsCount} Reels`}
              icon={<i className={`icon-tag ${iconsEnum.reels}`}></i>}
            />
          ) : null}
          {!user?.scope && campaign?.activation?.tweetsCount ? (
            <Tag
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: '0.9rem',
              }}
              labels={`${campaign?.activation?.tweetsCount} Tweets`}
              icon={<i className={`icon-tag ${iconsEnum.tweet}`}></i>}
            />
          ) : null}
          {!user?.scope && campaign?.activation?.tweetsRetweetsCount ? (
            <Tag
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: '0.9rem',
              }}
              labels={`${campaign?.activation?.tweetsRetweetsCount} Retweets`}
              icon={<i className={`icon-tag ${iconsEnum.retweet}`}></i>}
            />
          ) : null}
          {!user?.scope && campaign?.activation?.tweetsThreadCount ? (
            <Tag
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: '0.9rem',
              }}
              labels={`${campaign?.activation?.tweetsThreadCount} Threads`}
              icon={<i className={`icon-tag ${iconsEnum.tweetThread}`}></i>}
            />
          ) : null}
          {!user?.scope && campaign?.activation?.videosCount ? (
            <Tag
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: '0.9rem',
              }}
              labels={`${campaign?.activation?.videosCount} Videos`}
              icon={<i className={`icon-tag ${iconsEnum.video}`}></i>}
            />
          ) : null}
          {user?.status === 'recruited' && user?.scope ? getTagsScope() : null}
        </div>
      </div>
    </div>
  )

  let contentNps = campaign?.nps?.done ? (
    <Tag
      icon={<i className="fa-solid fa-check-circle" style={{ color: 'var(--green)' }}></i>}
      title={t('assessmentCompleted')}
      labels={<span className="small">{t('assessmentCompleted')}</span>}
    />
  ) : (
    <Button size="sm" onClick={handleClick}>
      <i className="fa-solid fa-star mr-1"></i>
      {t('nps')}
    </Button>
  )

  let listingTags = (
    <div className="tags">
      <Tag
        title={campaign?.socialNetworks[0]}
        style={{
          padding: '0.25rem 0.5rem',
          fontSize: '0.9rem',
          display: 'flex',
          alignItems: 'center',
          height: '100%',
        }}
        icon={<i className={`icon-tag m-0 fa-brands fa-${colorIconHelper.setIcon(campaign?.socialNetworks[0])}`}></i>}
      />
      {campaign?.startDate || campaign?.activation?.startDate ? (
        <Tag
          style={{
            padding: '0.25rem 0.5rem',
            fontSize: '0.9rem',
          }}
          // ? Os campos campaign.startDate e campaign.endDate são as datas de REALIZAÇÃO da campanhas
          //    * Esses valores estão vindo com timezone 0
          // ? Os campos campaign.activation.starDate e campaign.activation.endDate são as datas PREVISTAS da campanha
          //    * Esses valores estão vindo com timezone +3
          labels={`${t('startDate')} - ${new Date(startDate).toLocaleDateString(i18n.language, {
            timeZone: startDate?.getUTCHours() === 0 ? 'utc' : campaign?.activation?.timezone,
          })}`}
          icon={<i className={`icon-tag ${iconsEnum.startDate}`}></i>}
        />
      ) : null}
    </div>
  )

  if (mode === 'list') {
    content = (
      <div>
        {headerFull}
        <div className="content">{listingTags}</div>
      </div>
    )
  }

  if (mode === 'tiny') {
    listingTags = (
      <div className="tags">
        <Tag
          style={{
            padding: '0.25rem 0.5rem',
            fontSize: '0.9rem',
            lineHeight: '1.1',
          }}
          icon={
            <i
              className={`icon-tag m-0 ${
                !['tiktok', 'twitter']?.includes(campaign?.socialNetworks[0]) ? colorIconHelper.setColor(campaign?.socialNetworks[0]) : ''
              } fa-brands fa-${colorIconHelper.setIcon(campaign?.socialNetworks[0])}`}
            ></i>
          }
        />
        {campaign?.activation?.payments?.money ? (
          <Tag
            title={t('money')}
            style={{
              padding: '0.25rem 0.5rem',
              fontSize: '0.9rem',
            }}
            icon={<i className={`icon-tag ${iconsEnum.money} m-0`}></i>}
          />
        ) : null}
        {campaign?.activation?.payments?.kit ? (
          <Tag
            title={t('kit')}
            style={{
              padding: '0.25rem 0.5rem',
              fontSize: '0.9rem',
            }}
            icon={<i className={`icon-tag ${iconsEnum.kit} m-0`}></i>}
          />
        ) : null}
        {campaign?.activation?.payments?.products ? (
          <Tag
            title={t('products')}
            style={{
              padding: '0.25rem 0.5rem',
              fontSize: '0.9rem',
            }}
            icon={<i className={`icon-tag ${iconsEnum.products} m-0`}></i>}
          />
        ) : null}
        {campaign?.activation?.payments?.vouchers ? (
          <Tag
            title={t('vouchers')}
            style={{
              padding: '0.25rem 0.5rem',
              fontSize: '0.9rem',
            }}
            icon={<i className={`icon-tag ${iconsEnum.vouchers} m-0`}></i>}
          />
        ) : null}
      </div>
    )
    content = (
      <div className="wrapper">
        <figure className="avatar">
          <img
            src={
              (campaign?.activation?.brandLogo
                ? campaign?.activation?.brandPicture?.cdnUrl + campaign?.activation?.brandPicture?.url
                : campaign?.activation?.logo?.cdnUrl + campaign?.activation?.logo?.url) || ''
            }
            className="img-fluid"
            title="Campaign Avatar"
            alt="Campaign Avatar"
            onError={({ currentTarget }) => {
              currentTarget.onerror = null
              currentTarget.src = defaultImage
            }}
          />
        </figure>
        <h6 title={campaign?.exhibitionName || campaign?.title}>{campaign?.exhibitionName || campaign?.title}</h6>
        <div className="content">{['finished', 'archived'].includes(campaign?.status) && contentNps}</div>
      </div>
    )
  }

  if (!campaign) {
    return <div />
  }

  return (
    <div className={`campaign-head ${mode} ${className}`} style={style} id={id}>
      {content}
    </div>
  )
}
